import { forwardRef } from "react";
import { Link } from "react-router-dom";
import { Heart } from "lucide-react";
import Logo from "@/components/brand/Logo";
import CookieSettingsButton from "@/components/cookies/CookieSettingsButton";

const Footer = forwardRef<HTMLElement>((_, ref) => {
  return (
    <footer ref={ref} className="bg-white py-12 sm:py-16 mt-[50px] relative overflow-x-hidden border-t border-border/50">

      <div className="container mx-auto relative z-10 px-4">
        {/* Mobile: Centered, simplified layout */}
        <div className="sm:hidden text-center space-y-8">
          {/* Brand */}
          <div className="flex flex-col items-center">
            <Logo size="sm" linkTo="/" />
            <p className="text-muted-foreground text-sm font-body leading-relaxed mt-4 max-w-xs">
              Magiska, personliga barnböcker skapade med SnippSnapp-magi.
            </p>
          </div>

          {/* Links in 2 columns */}
          <div className="grid grid-cols-2 gap-6 text-sm text-left">
              <div className="space-y-4">
                <div className="space-y-2">
                  <p className="font-display font-bold text-foreground text-xs uppercase tracking-wide">Produkt</p>
                  <ul className="space-y-1.5">
                    <li><Link to="/how-it-works" className="text-muted-foreground hover:text-orange transition-colors">Så fungerar det</Link></li>
                    <li><Link to="/pricing" className="text-muted-foreground hover:text-orange transition-colors">Priser</Link></li>
                    <li><Link to="/about" className="text-muted-foreground hover:text-orange transition-colors">Om oss</Link></li>
                    <li><Link to="/news" className="text-muted-foreground hover:text-orange transition-colors">Nyheter</Link></li>
                  </ul>
                </div>
                <div className="space-y-2">
                  <p className="font-display font-bold text-foreground text-xs uppercase tracking-wide">Juridiskt</p>
                  <ul className="space-y-1.5">
                    <li><Link to="/privacy" className="text-muted-foreground hover:text-orange transition-colors">Integritetspolicy</Link></li>
                    <li><Link to="/terms" className="text-muted-foreground hover:text-orange transition-colors">Användarvillkor</Link></li>
                    <li><Link to="/cookies" className="text-muted-foreground hover:text-orange transition-colors">Cookiepolicy</Link></li>
                    <li><Link to="/security" className="text-muted-foreground hover:text-orange transition-colors">Säkerhet</Link></li>
                    <li>
                      <CookieSettingsButton className="text-muted-foreground hover:text-orange transition-colors text-left" />
                    </li>
                  </ul>
                </div>
              </div>
            <div className="space-y-2">
              <p className="font-display font-bold text-foreground text-xs uppercase tracking-wide">Support</p>
              <ul className="space-y-1.5">
                <li><Link to="/faq" className="text-muted-foreground hover:text-orange transition-colors">Hjälpcenter</Link></li>
                <li><Link to="/contact" className="text-muted-foreground hover:text-orange transition-colors">Kontakt</Link></li>
                <li><Link to="/faq" className="text-muted-foreground hover:text-orange transition-colors">FAQ</Link></li>
              </ul>
            </div>
          </div>

          {/* Bottom */}
          <div className="pt-6 border-t border-foreground/10 space-y-2">
            <p className="text-muted-foreground text-xs font-body">
              © {new Date().getFullYear()} SnippSnapp
            </p>
            <p className="text-muted-foreground text-xs font-body flex items-center justify-center gap-1">
              Gjord med <Heart className="w-3 h-3 text-orange fill-orange" /> i Sverige
            </p>
          </div>
        </div>

        {/* Desktop: Original layout */}
        <div className="hidden sm:block">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 sm:gap-12">
            {/* Brand */}
            <div className="sm:col-span-2 lg:col-span-1">
              <div className="mb-4">
                <Logo size="xs" linkTo="/" />
              </div>
              <p className="text-muted-foreground text-sm font-body leading-relaxed">
                Magiska, personliga barnböcker skapade med SnippSnapp-magi. 
                Gör ditt barn till hjälten i sin egen saga.
              </p>
            </div>

            {/* Links */}
            <div>
              <p className="font-display font-bold text-foreground mb-4">Produkt</p>
              <ul className="space-y-2">
                <li>
                  <Link to="/how-it-works" className="text-muted-foreground hover:text-orange transition-colors text-sm font-body">
                    Så fungerar det
                  </Link>
                </li>
                <li>
                  <Link to="/pricing" className="text-muted-foreground hover:text-orange transition-colors text-sm font-body">
                    Priser
                  </Link>
                </li>
                <li>
                  <Link to="/about" className="text-muted-foreground hover:text-orange transition-colors text-sm font-body">
                    Om oss
                  </Link>
                </li>
                <li>
                  <Link to="/news" className="text-muted-foreground hover:text-orange transition-colors text-sm font-body">
                    Nyheter
                  </Link>
                </li>
              </ul>
            </div>

            <div>
              <p className="font-display font-bold text-foreground mb-4">Support</p>
              <ul className="space-y-2">
                <li>
                  <Link to="/contact" className="text-muted-foreground hover:text-orange transition-colors text-sm font-body">
                    Kontakta oss
                  </Link>
                </li>
                <li>
                  <Link to="/faq" className="text-muted-foreground hover:text-orange transition-colors text-sm font-body">
                    Vanliga frågor
                  </Link>
                </li>
              </ul>
            </div>

            <div>
              <p className="font-display font-bold text-foreground mb-4">Juridiskt</p>
              <ul className="space-y-2">
                <li>
                  <Link to="/privacy" className="text-muted-foreground hover:text-orange transition-colors text-sm font-body">
                    Integritetspolicy
                  </Link>
                </li>
                <li>
                  <Link to="/terms" className="text-muted-foreground hover:text-orange transition-colors text-sm font-body">
                    Användarvillkor
                  </Link>
                </li>
                <li>
                  <Link to="/cookies" className="text-muted-foreground hover:text-orange transition-colors text-sm font-body">
                    Cookiepolicy
                  </Link>
                </li>
                <li>
                  <Link to="/security" className="text-muted-foreground hover:text-orange transition-colors text-sm font-body">
                    Säkerhet
                  </Link>
                </li>
                <li>
                  <CookieSettingsButton className="text-muted-foreground hover:text-orange transition-colors text-sm font-body text-left" />
                </li>
              </ul>
            </div>
          </div>

          {/* Bottom */}
          <div className="mt-12 pt-8 border-t border-foreground/10 flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-muted-foreground text-sm font-body">
              © {new Date().getFullYear()} SnippSnapp. Alla rättigheter förbehållna.
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
});

Footer.displayName = "Footer";

export default Footer;
